// Minecraft color + formatting code logic. Pure and dependency-light — reuses the
// canonical 16-color map (in §-code order) from the shared font module.
import { MC_TEXT_COLORS, getCharWidth } from '@/app/lib/minecraft/font';

export const SECTION = '§'; // §

// Color code char (0-9,a-f) derived from MC_TEXT_COLORS key order (black=0 … white=f).
export interface ColorEntry { code: string; name: string; hex: string }
export const COLOR_CODES: ColorEntry[] = Object.entries(MC_TEXT_COLORS).map(([name, hex], i) => ({
  code: i.toString(16),
  name,
  hex,
}));
const CODE_TO_COLOR: Record<string, ColorEntry> = Object.fromEntries(COLOR_CODES.map(c => [c.code, c]));

export interface FormatEntry { code: string; name: RunFormat | 'reset'; label: string }
export const FORMAT_CODES: FormatEntry[] = [
  { code: 'l', name: 'bold', label: 'Bold' },
  { code: 'o', name: 'italic', label: 'Italic' },
  { code: 'n', name: 'underline', label: 'Underline' },
  { code: 'm', name: 'strike', label: 'Strikethrough' },
  { code: 'k', name: 'obfuscated', label: 'Obfuscated' },
  { code: 'r', name: 'reset', label: 'Reset' },
];

export type RunFormat = 'bold' | 'italic' | 'underline' | 'strike' | 'obfuscated';

// ── Tokeniser ──────────────────────────────────────────────────────────────
type Token =
  | { t: 'color'; code: string; name: string }
  | { t: 'format'; code: string; name: RunFormat }
  | { t: 'hex'; hex: string } // #RRGGBB
  | { t: 'reset' }
  | { t: 'text'; text: string };

const HEX_SHORT = /^[&§]#([0-9a-fA-F]{6})/;
const LEGACY_HEX = /^[&§]x([&§][0-9a-fA-F]){6}/; // §x§R§R§G§G§B§B

// Accepts either &-codes or §-codes, plus &#RRGGBB / §#RRGGBB shorthand and the
// legacy §x§R§R… hex run.
//
// `&&` is a literal `&`. Before it, "R&B Night" could only ever mean R + an aqua "B Night", and
// a run whose TEXT held "R&B" (a server MOTD imported by the MOTD generator) was written out by
// runsToAmpersand as a code. `keepEscapes` leaves `&&` as is in the text, for the editor
// (RichTextInput), whose text is the user's source: there `&6` is a code and `&&` its escape.
// `sectionOnly`: text the GAME stores (world names, item names, build titles) — there only § is a
// code, and `&` is a plain character ("R&D base" must not turn light purple at the D), so `&&` is
// two of them.
export interface CodeOptions { sectionOnly?: boolean; keepEscapes?: boolean }
export function tokenize(input: string, opts: CodeOptions = {}): Token[] {
  const tokens: Token[] = [];
  let text = '';
  const flush = () => { if (text) { tokens.push({ t: 'text', text }); text = ''; } };

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch === '&' && input[i + 1] === '&' && !opts.sectionOnly) { text += opts.keepEscapes ? '&&' : '&'; i++; continue; }
    if (ch === SECTION || (ch === '&' && !opts.sectionOnly)) {
      const rest = input.slice(i);
      const hexShort = rest.match(HEX_SHORT);
      if (hexShort) { flush(); tokens.push({ t: 'hex', hex: '#' + hexShort[1].toUpperCase() }); i += 7; continue; }
      const legacy = rest.match(LEGACY_HEX);
      if (legacy) {
        flush();
        const hex = legacy[0].replace(/[&§x]/gi, '');
        tokens.push({ t: 'hex', hex: '#' + hex.toUpperCase() });
        i += legacy[0].length - 1;
        continue;
      }
      const next = input[i + 1]?.toLowerCase();
      if (next && CODE_TO_COLOR[next]) { flush(); tokens.push({ t: 'color', code: next, name: CODE_TO_COLOR[next].name }); i++; continue; }
      if (next === 'r') { flush(); tokens.push({ t: 'reset' }); i++; continue; }
      const fmt = FORMAT_CODES.find(f => f.code === next && f.name !== 'reset');
      if (fmt) { flush(); tokens.push({ t: 'format', code: fmt.code, name: fmt.name as RunFormat }); i++; continue; }
    }
    text += ch;
  }
  flush();
  return tokens;
}

// ── Styled runs (for preview / JSON / MiniMessage) ──────────────────────────
export interface Run {
  text: string;
  color?: string; // color name or #RRGGBB
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  obfuscated: boolean;
}
const emptyStyle = (): Omit<Run, 'text'> => ({ color: undefined, bold: false, italic: false, underline: false, strike: false, obfuscated: false });

// Vanilla semantics: a color (or hex) code resets all active formatting.
export function toRuns(input: string, opts: CodeOptions = {}): Run[] {
  const runs: Run[] = [];
  let style = emptyStyle();
  for (const tok of tokenize(input, opts)) {
    if (tok.t === 'text') { if (tok.text) runs.push({ text: tok.text, ...style }); }
    else if (tok.t === 'reset') style = emptyStyle();
    else if (tok.t === 'color') style = { ...emptyStyle(), color: tok.name };
    else if (tok.t === 'hex') style = { ...emptyStyle(), color: tok.hex };
    else if (tok.t === 'format') style = { ...style, [tok.name]: true };
  }
  return runs;
}

/** The text with every code removed: for `title`, aria-labels and anything else that can't be styled. */
export const stripCodes = (input: string, opts: CodeOptions = {}) => toRuns(input, opts).map(r => r.text).join('');

/**
 * Runs for the editor (RichTextInput) to SHOW: codes applied, `&&` kept as typed. Pairs with
 * `runsToAmpersand(runs, { escape: false })`, so repainting a value never changes it.
 */
export const toEditorRuns = (input: string) => toRuns(input, { keepEscapes: true });

/**
 * Rendered width of styled runs, in Minecraft font pixels.
 *
 * The vanilla font is VARIABLE width — `i` is 2px and `W` is 6 — so a character
 * count is a poor proxy for "will this fit on screen". Bold adds 1px per glyph,
 * which `getCharWidth` already accounts for.
 *
 * Color/format codes contribute nothing: they are not drawn.
 */
export function runsWidthPx(runs: Run[]): number {
  let width = 0;
  for (const run of runs) {
    for (let i = 0; i < run.text.length; i++) {
      width += getCharWidth(run.text.charCodeAt(i), run.bold);
    }
  }
  return width;
}

/** Same, for an `&`-coded string. */
export const textWidthPx = (coded: string) => runsWidthPx(toRuns(coded));

/**
 * The inverse of `toRuns` — styled runs back to an `&`-coded string.
 *
 * Exists so an editor can hold structured runs (what the user actually sees and
 * manipulates) while the rest of the app keeps working with `&` codes: every
 * exporter, the URL hash and the saved-element payload all speak that format, so
 * this is the boundary that keeps them unchanged.
 *
 * The one rule that matters: **a color code resets all active formatting**
 * (same vanilla semantic `toRuns` decodes on line ~84). So when a run has a
 * color we emit the color FIRST and then re-apply its formats — emitting them
 * the other way round would silently drop the bold/italic.
 *
 * Codes are emitted only when the style actually changes between runs, so
 * consecutive runs sharing a style stay compact.
 */
export function runsToAmpersand(runs: Run[], opts: { escape?: boolean } = {}): string {
  const escape = opts.escape ?? true;
  const styleKey = (r: Omit<Run, 'text'>) =>
    `${r.color ?? ''}|${r.bold}|${r.italic}|${r.underline}|${r.strike}|${r.obfuscated}`;
  const DEFAULT = styleKey(emptyStyle());
  const formatCodeOf = (name: RunFormat) => FORMAT_CODES.find(f => f.name === name)!.code;
  const colorCodeOf = (color: string) =>
    color.startsWith('#') ? color : COLOR_CODES.find(c => c.name === color)?.code;

  let out = '';
  let prev = DEFAULT;

  for (const run of runs) {
    if (run.text === '') continue;
    const key = styleKey(run);

    if (key !== prev) {
      let codes = '';
      if (run.color) {
        const code = colorCodeOf(run.color);
        // An unknown color name would otherwise emit "&undefined" into the
        // command; drop the color rather than corrupt the output.
        if (code) codes += '&' + code;
        else if (prev !== DEFAULT) codes += '&r';
      } else if (prev !== DEFAULT) {
        // No color to do the resetting for us, and something was active.
        codes += '&r';
      }
      // Re-applied AFTER the color, because the color cleared them.
      for (const f of ['bold', 'italic', 'underline', 'strike', 'obfuscated'] as RunFormat[]) {
        if (run[f]) codes += '&' + formatCodeOf(f);
      }
      // With escape: false, a run can end in a lone `&` ("Rock &"). Followed straight by a code it
      // would form `&&` + "9", the escape, and the code would be lost. Double that last `&`.
      if (codes && /(?:^|[^&])(?:&&)*&$/.test(out)) out += '&';
      out += codes;
      prev = key;
    }

    // A literal `&` is written as `&&` (see tokenize), or "R&B" would come back as R + aqua.
    // `escape: false` is for the editor, whose text is source (`&6` typed = a code).
    out += escape ? run.text.replace(/&/g, '&&') : run.text;
  }

  return out;
}

// ── Serializers ─────────────────────────────────────────────────────────────
const hexToLegacy = (prefix: string, hex: string) =>
  prefix + 'x' + hex.replace('#', '').split('').map(h => prefix + h).join('');

const rgbOf = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

/** The NAME of the nearest of the 16 vanilla colors to an #RRGGBB, by RGB distance. */
export function nearestNamedColor(hex: string): string {
  const [r, g, b] = rgbOf(hex);
  let best = COLOR_CODES[0];
  let bestD = Infinity;
  for (const c of COLOR_CODES) {
    const [cr, cg, cb] = rgbOf(c.hex);
    const d = (r - cr) ** 2 + (g - cg) ** 2 + (b - cb) ** 2;
    if (d < bestD) { bestD = d; best = c; }
  }
  return best.name;
}
const nearestCode = (hex: string) => COLOR_CODES.find(c => c.name === nearestNamedColor(hex))!.code;

/**
 * How a hex color is written in a § or & string. None of these is vanilla: a vanilla client reads
 * `§x§F§F§8§8§0§0` as six ordinary color codes and shows the LAST one (SPIGOT-5851), so vanilla
 * text can only carry hex as a text component's `color` field.
 *   - `legacy`  — `§x§R§R§G§G§B§B` / `&x&R…`: Spigot, Paper, BungeeCord (Bukkit's
 *                 translateAlternateColorCodes understands the & form).
 *   - `hash`    — `&#RRGGBB`: EssentialsX, CMI and similar plugins only. & output only.
 *   - `nearest` — the nearest of the 16 named colors, for vanilla.
 */
export type HexStyle = 'legacy' | 'hash' | 'nearest';

/** § form. `hex` defaults to `legacy` (Spigot/Paper); pass `nearest` for vanilla. */
export function toSection(input: string, opts: { hex?: Exclude<HexStyle, 'hash'> } = {}): string {
  const hex = opts.hex ?? 'legacy';
  return tokenize(input).map(tok => {
    switch (tok.t) {
      case 'text': return tok.text;
      case 'reset': return SECTION + 'r';
      case 'color': return SECTION + tok.code;
      case 'format': return SECTION + tok.code;
      case 'hex': return hex === 'nearest' ? SECTION + nearestCode(tok.hex) : hexToLegacy(SECTION, tok.hex);
    }
  }).join('');
}

/**
 * The `&` form a server plugin translates (vanilla ignores `&`). `hex` defaults to `hash`
 * (`&#RRGGBB`). Plugins have no escape for a literal `&`, so text such as "R&B" will still be read
 * as a code by them; nothing in this string can prevent that.
 */
export function toAmpersand(input: string, opts: { hex?: HexStyle } = {}): string {
  const hex = opts.hex ?? 'hash';
  return tokenize(input).map(tok => {
    switch (tok.t) {
      case 'text': return tok.text;
      case 'reset': return '&r';
      case 'color': return '&' + tok.code;
      case 'format': return '&' + tok.code;
      case 'hex':
        return hex === 'nearest' ? '&' + nearestCode(tok.hex) : hex === 'legacy' ? hexToLegacy('&', tok.hex) : '&' + tok.hex;
    }
  }).join('');
}

/** True if the coded string carries an RGB hex color (`&#RRGGBB` / `§x…`). */
export const hasHex = (input: string): boolean => tokenize(input).some(t => t.t === 'hex');

/**
 * Escaped-unicode form for JSON strings and a server.properties MOTD.
 *
 * Escapes EVERY non-ASCII UTF-16 code unit as `\uXXXX`, not just `§`.
 *
 * Java's `Properties.load` reads the file as **ISO-8859-1**, so only U+0000–U+00FF
 * survive as literal bytes — and only if the editor actually saved it in that
 * encoding. Editors default to UTF-8 these days, which turns any literal non-ASCII
 * character into multiple bytes that Java then reads as separate Latin-1 chars:
 * classic mojibake. Escaping everything above ASCII makes the output byte-identical
 * under both encodings, so it cannot be corrupted by how the file gets saved.
 *
 * Iterating by CODE UNIT (charCodeAt, not for…of) is deliberate: characters above
 * the BMP — most emoji — are stored as a surrogate pair, and Java expects that pair
 * written as TWO four-digit escapes (the grinning face becomes the D83D escape
 * followed by the DE00 escape). Iterating by code point instead would produce one
 * five-digit escape, which Java reads as a four-digit escape plus a stray digit.
 *
 * Previously this escaped only `§`, so an emoji/CJK/Cyrillic MOTD arrived mangled on
 * a real server.
 */
export function toUnicode(input: string): string {
  const sectioned = toSection(input);
  let out = '';
  for (let i = 0; i < sectioned.length; i++) {
    const code = sectioned.charCodeAt(i);
    out += code > 0x7e
      ? '\\u' + code.toString(16).toUpperCase().padStart(4, '0')
      : sectioned[i];
  }
  return out;
}

interface JsonComp { text: string; color?: string; bold?: true; italic?: true; underlined?: true; strikethrough?: true; obfuscated?: true }

/** The raw text-component VALUE (object or `["",…]` array) — not yet serialized. */
export type TextComponentValue = JsonComp | Array<string | JsonComp>;

/**
 * Build the Minecraft text-component VALUE from an &-coded string.
 *
 * Kept separate from `toJson` so callers that must be VERSION-CORRECT can pass
 * this straight to `renderComponentArgument`/`renderTextComponent` in
 * textComponent.ts, which serialize it per era (JSON <1.21.5, bare SNBT after).
 * A generator that stringifies here itself (as `toJson` does) is locked to the
 * pre-1.21.5 JSON form — fine only where the surrounding grammar accepts it.
 */
export function toComponent(input: string): TextComponentValue {
  const comps: JsonComp[] = toRuns(input).map(r => {
    const c: JsonComp = { text: r.text };
    if (r.color) c.color = r.color;
    if (r.bold) c.bold = true;
    if (r.italic) c.italic = true;
    if (r.underline) c.underlined = true;
    if (r.strike) c.strikethrough = true;
    if (r.obfuscated) c.obfuscated = true;
    return c;
  });
  // A leading "" so the array's own (empty) style doesn't color the children.
  return comps.length === 0 ? { text: '' } : comps.length === 1 ? comps[0] : ['', ...comps];
}

/** Pre-1.21.5 JSON serialization. Version-agnostic callers only — see toComponent. */
export function toJson(input: string): string {
  return JSON.stringify(toComponent(input));
}

// MiniMessage treats <…> as tags, so literal text must escape '<' (and the
// escape character itself) or user text like "<world>" would be parsed as a tag.
export function escapeMiniMessage(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/</g, '\\<');
}

// MiniMessage — each run wrapped in its full tag set (verbose but always valid).
export function toMiniMessage(input: string): string {
  return toRuns(input).map(r => {
    const tags: string[] = [];
    if (r.color) tags.push(r.color); // named color, or #RRGGBB which is a valid tag
    if (r.bold) tags.push('bold');
    if (r.italic) tags.push('italic');
    if (r.underline) tags.push('underlined');
    if (r.strike) tags.push('strikethrough');
    if (r.obfuscated) tags.push('obfuscated');
    const text = escapeMiniMessage(r.text);
    if (tags.length === 0) return text;
    const open = tags.map(t => `<${t}>`).join('');
    const close = [...tags].reverse().map(t => `</${t}>`).join('');
    return open + text + close;
  }).join('');
}
